import { useEffect, useRef, useState } from "react";

import StorebrandKeycloak from "@storebrand-digital/storebrand-auth";

// How many seconds from refresh token expiration to display timout warning modal.
const TIMEOUT_WARNING_THRESHOLD = 300;

// How often to check time left on refresh token.
const IDLE_CHECK_INTERVAL = 1000;

const getTimestampDifferenceInSeconds = (timeX: number, timeY: number) => {
  return timeX - timeY;
};

const useAuthTimeout = () => {
  const { authenticated, logout } = StorebrandKeycloak;

  const expirationTime = useRef<number>();

  const [warningTimeReached, setWarningTimeReached] = useState<boolean>(false);
  const [expiryTimeReached, setExpiryTimeReacted] = useState<boolean>(false);

  const getRefreshTokenExpirationTime = () => {
    return StorebrandKeycloak.getInstance()?.refreshTokenParsed?.exp;
  };

  const forceTokenUpdate = () => {
    StorebrandKeycloak.getInstance()
      ?.updateToken(60)
      .then((updateSuccessful: boolean) => {
        if (updateSuccessful) {
          const { refreshTokenParsed } = StorebrandKeycloak.getInstance() ?? {};
          if (expirationTime.current) {
            expirationTime.current = refreshTokenParsed?.exp;
          }
        }
      })
      .catch(() => {
        if (!authenticated()) {
          StorebrandKeycloak.login();
        }
      });
  };

  useEffect(() => {
    expirationTime.current = getRefreshTokenExpirationTime();

    const interval = setInterval(() => {
      if (!authenticated()) {
        logout();
      }

      if (expirationTime.current !== getRefreshTokenExpirationTime()) {
        expirationTime.current = getRefreshTokenExpirationTime();
      }

      if (expirationTime && expirationTime.current) {
        const secondsUntilAuthExpiration = getTimestampDifferenceInSeconds(
          expirationTime.current,
          Math.floor(Date.now() / 1000),
        );

        if (
          secondsUntilAuthExpiration <= TIMEOUT_WARNING_THRESHOLD &&
          secondsUntilAuthExpiration > 0
        ) {
          setWarningTimeReached(true);
        }

        if (secondsUntilAuthExpiration <= 0) {
          setExpiryTimeReacted(true);
          clearInterval(interval);
        }
      }
    }, IDLE_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  return {
    expiryTimeReached,
    warningTimeReached,
    forceTokenUpdate,
    setWarningTimeReached,
  };
};

export default useAuthTimeout;
